import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, BookOpen, Star, X, ArrowRight, ArrowLeft, Check } from "lucide-react";

const KEY = "tt-welcome-seen-v1";

const STEPS = [
  {
    icon: Mic,
    title: "Speak instead of typing",
    text: "See a blue “Tap to speak” button? Press it and simply say what you need — like “router” or “I can't send email.” We'll turn your voice into text for you. No typing required.",
  },
  {
    icon: BookOpen,
    title: "Look up any word",
    text: "Confused by a tech word? Visit our Tech Glossary for simple, plain‑English meanings. You can even tap “Read aloud” to have each one read to you out loud.",
  },
  {
    icon: Star,
    title: "See & leave ratings",
    text: "Read honest reviews from your neighbors on our Testimonials page — and once we've helped you, tap the stars to leave your own rating. It only takes a moment.",
  },
];

export const WelcomeWalkthrough = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem(KEY) === "1";
    } catch (err) {
      console.warn("Could not read welcome-seen flag.", err);
    }
    if (!seen) {
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener("open-welcome", handler);
    return () => window.removeEventListener("open-welcome", handler);
  }, []);

  const finish = useCallback(() => {
    try {
      localStorage.setItem(KEY, "1");
    } catch (err) {
      console.warn("Could not save welcome-seen flag.", err);
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) finish();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, finish]);

  const isLast = step === STEPS.length - 1;
  const s = STEPS[step];
  const Icon = s?.icon;

  const done = () => {
    finish();
    navigate("/glossary");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] grid place-items-center p-4 bg-primary/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={finish}
          data-testid="welcome-overlay"
        >
          <motion.div
            role="dialog"
            aria-label="Welcome walkthrough"
            data-testid="welcome-modal"
            className="relative w-full max-w-lg rounded-3xl bg-card shadow-2xl p-8 sm:p-10"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="welcome-skip"
              onClick={finish}
              aria-label="Close welcome"
              className="absolute top-5 right-5 grid place-items-center h-10 w-10 rounded-full text-muted-foreground hover:bg-muted"
            >
              <X className="h-6 w-6" />
            </button>

            <p className="text-accent font-bold uppercase tracking-wide text-sm">
              Welcome — Step {step + 1} of {STEPS.length}
            </p>

            <motion.div
              key={step}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <span className="mt-5 grid place-items-center h-20 w-20 rounded-2xl bg-accent/10 text-accent">
                {Icon && <Icon className="h-10 w-10" />}
              </span>
              <h2 className="mt-6 font-heading font-extrabold text-3xl text-foreground leading-tight">
                {s.title}
              </h2>
              <p className="mt-4 text-muted-foreground text-lg leading-relaxed">{s.text}</p>
            </motion.div>

            {/* Dots */}
            <div className="mt-8 flex items-center gap-2" data-testid="welcome-dots">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-2.5 rounded-full transition-all ${
                    i === step ? "w-8 bg-accent" : "w-2.5 bg-border"
                  }`}
                />
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between gap-3">
              {step > 0 ? (
                <button
                  data-testid="welcome-back"
                  onClick={() => setStep((v) => v - 1)}
                  className="inline-flex items-center gap-2 h-14 px-6 rounded-full border border-border text-foreground font-bold text-lg hover:border-accent transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" /> Back
                </button>
              ) : (
                <button
                  data-testid="welcome-skip-text"
                  onClick={finish}
                  className="h-14 px-4 text-muted-foreground font-semibold text-lg hover:text-foreground transition-colors"
                >
                  Skip
                </button>
              )}

              {isLast ? (
                <button
                  data-testid="welcome-done"
                  onClick={done}
                  className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors"
                >
                  <Check className="h-5 w-5" /> Explore the Glossary
                </button>
              ) : (
                <button
                  data-testid="welcome-next"
                  onClick={() => setStep((v) => v + 1)}
                  className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors"
                >
                  Next <ArrowRight className="h-5 w-5" />
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
